import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Card, Button } from '../../components/ui';
import {
  Image as ImageIcon,
  Upload,
  Download,
  RefreshCw,
  Sparkles,
  Eraser,
  Brush,
} from 'lucide-react';
import { cn } from '../../lib/utils';

import { setDpiInPngBase64, getBackgroundModel } from '../../lib/imageUtils';
import {
  processImage as processImageOnWorker,
  warmEngine,
  isUsingWorker,
  disposeWorker,
} from '../../lib/inpaint/workerClient';
import type { PipelineStage } from '../../lib/inpaint/pipeline';
import { WatermarkMaskEditor } from './WatermarkMaskEditor';
import { buildStarMask, countMask, createMask, extractTemplateAlpha } from '../../lib/inpaint/mask';
import { DEFAULT_INPAINT_RADIUS, type InpaintMethod } from '../../lib/inpaint/inpaint';

/**
 * Removal approaches. Only these three exist — every entry is implemented.
 *
 * The previous version of this component offered nine methods in the dropdown
 * but only implemented four; the other five silently did nothing except upscale
 * the image, which is a considerably worse failure mode than not offering them.
 */
type RemovalMode = 'inpaint' | 'crop' | 'none';

/**
 * The Gemini star sits a fixed *pixel* distance from the bottom-right corner
 * regardless of resolution, so it is expressed in pixels rather than as a
 * percentage. (It was 3.1% of the width at 1024px but 5.6% at 576px, which is
 * what broke the original percentage-based positioning.)
 */
const GEMINI_STAR_OFFSET_PX = 32;

/**
 * Reference resolution the star template was captured at. The template is a
 * 128x128 crop centred on the star, so this converts template pixels to image
 * pixels via `min(w, h) / GEMINI_TEMPLATE_REFERENCE`.
 */
const GEMINI_TEMPLATE_REFERENCE = 576;

/** Larger is a tighter mask (only the star core); smaller catches more glow. */
const DEFAULT_COVERAGE = 0.12;
const DEFAULT_GROW = 2;

type EngineState = 'idle' | 'loading' | 'ready' | 'failed';

export function ImageUpscalerTool() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [resultBytes, setResultBytes] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);

  // ── Removal configuration ────────────────────────────────────────────────
  const [removalMode, setRemovalMode] = useState<RemovalMode>('inpaint');
  const [mask, setMask] = useState<Uint8Array | null>(null);
  const [coverage, setCoverage] = useState(DEFAULT_COVERAGE);

  /**
   * The mask is a `Uint8Array` that the brush mutates in place — doing that
   * correctly at 60 fps means not allocating a new array per pointermove. React
   * cannot see those mutations, so a counter is dispatched to force a re-render
   * whenever the mask content changed. A reducer is used because the value is
   * never read, only its identity of updates matters.
   */
  const [, forceMaskRedraw] = useReducer((n: number) => n + 1, 0);
  const [grow, setGrow] = useState(DEFAULT_GROW);
  const [brushRadius, setBrushRadius] = useState(24);
  const [erasing, setErasing] = useState(false);
  const [inpaintRadius, setInpaintRadius] = useState(DEFAULT_INPAINT_RADIUS);
  const [inpaintMethod, setInpaintMethod] = useState<InpaintMethod>('telea');
  const [cropSize, setCropSize] = useState(120);
  const [starCenter, setStarCenter] = useState<{ x: number; y: number } | null>(null);

  // ── Output configuration ─────────────────────────────────────────────────
  const [bgRemoval, setBgRemoval] = useState<'none' | 'magic-wand'>('none');
  const [bgTolerance, setBgTolerance] = useState(15);
  const [scaleFactor, setScaleFactor] = useState(2);
  const [targetDpi, setTargetDpi] = useState(300);
  const [sharpenAmount, setSharpenAmount] = useState(0.45);
  const [originalFilename, setOriginalFilename] = useState<string>('upscaled-image');

  const [isDragging, setIsDragging] = useState(false);
  const [engineState, setEngineState] = useState<EngineState>('idle');
  /** Guards against kicking off duplicate loads while one is in flight. */
  const enginePending = useRef(false);

  const maskedPixels = mask ? countMask(mask) : 0;
  /** True when processing runs in a worker rather than on the main thread. */
  const usingWorker = isUsingWorker();

  /**
   * Star template alpha, loaded once and reused. Every coverage/grow slider tick
   * re-runs auto-detect, so re-fetching and re-decoding the PNG each time would
   * also let slow loads resolve out of order.
   */
  const templateRef = useRef<Promise<{ alpha: Uint8Array; width: number; height: number }> | null>(null);
  /** Bumped per auto-detect call; a stale call whose number no longer matches bails out. */
  const autoSeqRef = useRef(0);
  /** The star mask most recently merged in, so the next run can swap it out. */
  const lastAutoMaskRef = useRef<Uint8Array | null>(null);

  const resetForNewImage = useCallback(() => {
    setResultBytes(null);
    setResult((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
    setError(null);
    setMask(null);
    forceMaskRedraw();
    // The star auto-detect state is cleared here so a new file does not inherit
    // the previous image's mask or sequence.
    lastAutoMaskRef.current = null;
    autoSeqRef.current++;
    setStarCenter(null);
    setImageWidth(0);
    setImageHeight(0);
  }, []);

  const processFile = useCallback(
    (file?: File) => {
      if (!file) return;
      setOriginalFilename(file.name.replace(/\.[^/.]+$/, ''));
      resetForNewImage();
      const reader = new FileReader();
      reader.onload = (event) => setSelectedImage(event.target?.result as string);
      reader.readAsDataURL(file);
    },
    [resetForNewImage]
  );

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFile(e.target.files?.[0]);
    // Without this, re-picking the same file does not fire onChange.
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) processFile(file);
  };

  /**
   * Star template alpha, loaded once and reused. Every coverage/grow slider tick
   * re-runs auto-detect, so re-fetching and re-decoding the PNG each time would
   * also let slow loads resolve out of order.
   */
  const loadTemplate = useCallback(() => {
    if (!templateRef.current) {
      templateRef.current = (async () => {
        const image = new Image();
        image.src = '/gemini_star_alpha.png';
        await new Promise<void>((resolve, reject) => {
          image.onload = () => resolve();
          image.onerror = () => reject(new Error('Could not load the watermark template.'));
        });

        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not read the watermark template.');
        ctx.drawImage(image, 0, 0);
        const templateData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        return {
          alpha: extractTemplateAlpha(templateData.data, canvas.width, canvas.height),
          width: canvas.width,
          height: canvas.height,
        };
      })();
      // Allow a retry on the next call if this load fails.
      templateRef.current.catch(() => {
        templateRef.current = null;
      });
    }
    return templateRef.current;
  }, []);

  /**
   * Turn the star template into a mask for the current image.
   *
   * The pixels from the previous auto-detect run are removed before the new
   * star mask is merged in. Without that, the merge could only ever add pixels,
   * so raising coverage ("tighter") never shrank the mask. Hand-painted pixels
   * outside the old star region are kept. Painted pixels *inside* it are
   * indistinguishable from the old star and get replaced along with it.
   *
   * Always returns a new array: the editor redraws its overlay when the mask's
   * identity changes, and mutating in place left it showing stale pixels.
   */
  const applyAutoStarMask = useCallback(
    async (width: number, height: number, coverageValue: number, growValue: number) => {
      const seq = ++autoSeqRef.current;
      try {
        const template = await loadTemplate();
        if (seq !== autoSeqRef.current) return;

        const centre = { x: width - GEMINI_STAR_OFFSET_PX, y: height - GEMINI_STAR_OFFSET_PX };
        setStarCenter(centre);

        const built = buildStarMask(width, height, {
          template: template.alpha,
          templateWidth: template.width,
          templateHeight: template.height,
          centerX: centre.x,
          centerY: centre.y,
          scale: Math.min(width, height) / GEMINI_TEMPLATE_REFERENCE,
          coverage: coverageValue,
          grow: growValue,
        });

        // Read and update the ref outside the state updater so the updater stays pure
        // (StrictMode calls updaters twice).
        const previousAuto = lastAutoMaskRef.current;
        lastAutoMaskRef.current = built ? built.mask : null;

        setMask((existing) => {
          const size = width * height;
          const base = existing && existing.length === size ? existing.slice() : createMask(width, height);
          if (previousAuto && previousAuto.length === size) {
            for (let i = 0; i < size; i++) if (previousAuto[i]) base[i] = 0;
          }
          if (built) {
            for (let i = 0; i < size; i++) if (built.mask[i]) base[i] = 255;
          }
          return base;
        });
        forceMaskRedraw();
      } catch (cause) {
        if (seq !== autoSeqRef.current) return;
        setError(
          cause instanceof Error
            ? cause.message
            : 'Could not auto-detect the watermark. Paint a mask over it instead.'
        );
      }
    },
    [loadTemplate]
  );

  const handleImageLoad = useCallback(
    (img: HTMLImageElement) => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      setImageWidth(width);
      setImageHeight(height);
      void applyAutoStarMask(width, height, coverage, grow);
    },
    [applyAutoStarMask, coverage, grow]
  );

// Warm the inpainting engine in the worker as soon as it could be needed, so
  // the one-time ~10 MB download and WASM compile overlap with the user deciding
  // what to remove instead of stalling the click that actually does the work.
  //
  // State is only set from the promise callbacks, never synchronously in the
  // effect body, so enabling inpainting does not trigger a cascading render.
  useEffect(() => {
    if (removalMode !== 'inpaint' || !selectedImage) return;
    if (enginePending.current) return;

    enginePending.current = true;
    let cancelled = false;

    // Defer so 'loading' is set outside the synchronous effect body.
    queueMicrotask(() => {
      if (!cancelled) setEngineState('loading');
    });

    warmEngine()
      .then((ok) => {
        enginePending.current = false;
        if (!cancelled) setEngineState(ok ? 'ready' : 'failed');
      })
      .catch(() => {
        enginePending.current = false;
        if (!cancelled) setEngineState('failed');
      });

    return () => {
      cancelled = true;
      enginePending.current = false;
    };
  }, [removalMode, selectedImage]);

  // Release the worker on unmount so its ~10 MB runtime and buffers are not
  // retained for the rest of the session.
  useEffect(() => () => disposeWorker(), []);

  const handleMaskChange = useCallback(() => forceMaskRedraw(), []);

const onPipelineStage = (stage: PipelineStage) => {
    switch (stage) {
      case 'inpaint':
        return setStatus('Reconstructing the masked area…');
      case 'upscale':
        return setStatus('Upscaling…');
      case 'sharpen-blur':
      case 'sharpen-combine':
        return setStatus('Sharpening…');
      case 'encode':
        return setStatus('Encoding PNG…');
      default:
        return undefined;
    }
  };

  const processImage = async () => {
    const img = imgRef.current;
    if (!img || !selectedImage) return;

    setIsProcessing(true);
    setError(null);
    setStatus('Preparing…');

    try {
      const sourceWidth = img.naturalWidth;
      const sourceHeight = img.naturalHeight;
      const removing = removalMode === 'inpaint';

      if (removing && maskedPixels === 0) {
        throw new Error('Nothing is selected for removal. Paint over the watermark first.');
      }

      // Read the source once, on the main thread. Everything expensive after
      // this point happens in the worker.
      const source = document.createElement('canvas');
      source.width = sourceWidth;
      source.height = sourceHeight;
      const sourceCtx = source.getContext('2d', { willReadFrequently: true });
      if (!sourceCtx) throw new Error('Could not get a 2D canvas context.');
      sourceCtx.drawImage(img, 0, 0);

      let pixels: Uint8ClampedArray;
      let maskToSend: Uint8Array;
      let workWidth: number;
      let workHeight: number;

      if (removing) {
        // The mask lives in full-image coordinates, so send the whole frame and
        // let the pipeline work in that space.
        pixels = sourceCtx.getImageData(0, 0, sourceWidth, sourceHeight).data;
        maskToSend = mask!;
        workWidth = sourceWidth;
        workHeight = sourceHeight;
      } else {
        // Cropping discards the corner entirely, so there is nothing to
        // inpaint — take just the region and send an empty mask.
        const centre = starCenter ?? {
          x: sourceWidth - GEMINI_STAR_OFFSET_PX,
          y: sourceHeight - GEMINI_STAR_OFFSET_PX,
        };
        workWidth =
          removalMode === 'crop'
            ? Math.max(1, Math.floor(centre.x - cropSize / 2))
            : sourceWidth;
        workHeight =
          removalMode === 'crop'
            ? Math.max(1, Math.floor(centre.y - cropSize / 2))
            : sourceHeight;
        pixels = sourceCtx.getImageData(0, 0, workWidth, workHeight).data;
        maskToSend = new Uint8Array(workWidth * workHeight);
      }

      // Optional background knock-out. Done here rather than in the worker
      // because it is cheap and needs the same pixels we already have.
      if (bgRemoval === 'magic-wand') {
        setStatus('Removing background…');
        await new Promise((resolve) => setTimeout(resolve, 0));
        knockOutBackgroundPixels(pixels, workWidth, workHeight, bgTolerance);
      }

      // Inpaint + upscale + sharpen + encode, all off the main thread.
      const { png, result } = await processImageOnWorker(
        { data: pixels, width: workWidth, height: workHeight, mask: maskToSend },
        {
          scaleFactor,
          sharpenAmount,
          inpaintRadius,
          inpaintMethod,
          targetDpi,
          onStage: onPipelineStage,
        }
      );

      let url: string;
      if (png) {
        // The worker already encoded and DPI-stamped it, so all that crosses
        // back is the compressed file.
        url = URL.createObjectURL(new Blob([png], { type: 'image/png' }));
      } else {
        // Fallback for environments without OffscreenCanvas in the worker.
        setStatus('Encoding PNG…');
        const canvas = document.createElement('canvas');
        canvas.width = result.width;
        canvas.height = result.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get a 2D canvas context.');
        ctx.putImageData(
          new ImageData(new Uint8ClampedArray(result.data), result.width, result.height),
          0,
          0
        );
        const encoded = canvas.toDataURL('image/png');
        url = targetDpi > 0 ? setDpiInPngBase64(encoded, targetDpi) : encoded;
      }

      // Release the previous object URL, otherwise every run leaks the whole
      // encoded image.
      setResult((previous) => {
        if (previous?.startsWith('blob:')) URL.revokeObjectURL(previous);
        return url;
      });
      setResultBytes(png ? png.byteLength : Math.round((url.length * 3) / 4));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Something went wrong while processing.');
    } finally {
      setIsProcessing(false);
      setStatus(null);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const link = document.createElement('a');
    link.download = `${originalFilename}-upscaled.png`;
    link.href = result;
    link.click();
  };

  const canProcess =
    !!selectedImage && !isProcessing && (removalMode !== 'inpaint' || maskedPixels > 0);

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      <div className="space-y-6">
        <Card className="p-6 space-y-4 border border-gray-150">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Upload className="text-blue-500" size={20} /> Upload Image
          </h3>
          <p className="text-xs text-gray-500">
            Upload an image, mark whatever watermark you want gone, and upscale the result. Everything
            runs in your browser — no image is uploaded to a server.
          </p>

          <div
            className={cn(
              'border-2 border-dashed rounded-xl p-8 text-center transition-colors relative',
              !selectedImage && 'cursor-pointer',
              isDragging
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-900'
            )}
            // Only before an image is loaded: the mask editor lives inside this box,
            // and its brush strokes and Undo/Clear clicks would otherwise bubble up
            // and open the file picker.
            onClick={() => {
              if (!selectedImage) fileInputRef.current?.click();
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleImageUpload}
            />
            {selectedImage ? (
              <WatermarkMaskEditor
                imageSrc={selectedImage}
                imageWidth={imageWidth}
                imageHeight={imageHeight}
                mask={mask}
                onMaskChange={handleMaskChange}
                brushRadius={brushRadius}
                erasing={erasing}
                editable={removalMode === 'inpaint' && imageWidth > 0}
                onImageLoad={handleImageLoad}
                imgRef={imgRef}
              />
            ) : (
              <div className="flex flex-col items-center justify-center space-y-3">
                <ImageIcon className="w-10 h-10 text-gray-400" />
                <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                  {isDragging ? 'Drop image here' : 'Click or drag image to upload'}
                </span>
              </div>
            )}
          </div>

          {selectedImage && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer"
            >
              <Upload size={14} /> Choose a different image
            </button>
          )}

          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-800 p-3 text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">
                Removal Method
              </label>
              <select
                value={removalMode}
                onChange={(e) => setRemovalMode(e.target.value as RemovalMode)}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white dark:bg-slate-950 dark:border-slate-800 focus:ring-1 focus:ring-blue-500 outline-none cursor-pointer"
              >
                <option value="inpaint">Inpaint — reconstruct from surrounding pixels</option>
                <option value="crop">Crop — trim the corner off</option>
                <option value="none">None — just upscale</option>
              </select>
              <p className="text-[11px] text-gray-400 mt-1">
                Inpainting marks the watermark as unknown and synthesises a plausible continuation
                of the surrounding image. It never tries to recover the original pixels underneath,
                because on a JPEG those are gone.
              </p>
            </div>

            {removalMode === 'inpaint' && (
              <div className="col-span-2 space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700 dark:text-blue-300 text-xs font-semibold">
                      {maskedPixels > 0
                        ? `${maskedPixels.toLocaleString()} px selected for removal`
                        : 'Nothing selected yet'}
                    </span>
                    {engineState === 'loading' && (
                      <span className="text-[10px] text-blue-500 flex items-center gap-1">
                        <RefreshCw size={11} className="animate-spin" />
                        Loading engine…
                      </span>
                    )}
{engineState === 'failed' && (
                      <span className="text-[10px] text-red-500">Engine failed to load</span>
                    )}
                    {engineState === 'ready' && usingWorker && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400">
                        Ready (background thread)
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-blue-500 dark:text-blue-400 mt-1">
                    The red overlay is the area that will be reconstructed. Paint to add to it, use
                    the eraser to trim it back.
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setErasing(false)}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold cursor-pointer',
                      !erasing
                        ? 'border-blue-500 bg-blue-500 text-white'
                        : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800'
                    )}
                  >
                    <Brush size={14} /> Paint
                  </button>
                  <button
                    type="button"
                    onClick={() => setErasing(true)}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold cursor-pointer',
                      erasing
                        ? 'border-blue-500 bg-blue-500 text-white'
                        : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800'
                    )}
                  >
                    <Eraser size={14} /> Erase
                  </button>
                </div>

                <div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Brush size</span>
                    <span className="font-mono">{brushRadius}px</span>
                  </div>
                  <input
                    type="range"
                    min="4"
                    max="120"
                    value={brushRadius}
                    onChange={(e) => setBrushRadius(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-800 accent-blue-600"
                  />
                </div>

                <div className="border-t border-gray-100 dark:border-slate-800 pt-4 space-y-4">
                  <label className="text-[10px] uppercase font-bold text-gray-400 block">
                    Gemini Star Auto-Detect
                  </label>
                  <p className="text-[11px] text-gray-500">
                    Automatically marks the star Gemini adds to the bottom-right of generated images.
                    The template only covers the main star, so paint over the small companion star
                    next to it.
                  </p>
                  <button
                    type="button"
                    onClick={() => imageWidth > 0 && applyAutoStarMask(imageWidth, imageHeight, coverage, grow)}
                    className="w-full rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-xs font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900 cursor-pointer"
                  >
                    Auto-detect star
                  </button>

                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Coverage</span>
                      <span className="font-mono">{(coverage * 100).toFixed(0)}%</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mb-1">
                      Lower catches more of the faint outer glow; higher is tighter to the star body.
                    </p>
                    <input
                      type="range"
                      min="2"
                      max="60"
                      value={Math.round(coverage * 100)}
                      onChange={(e) => {
                        const next = Number(e.target.value) / 100;
                        setCoverage(next);
                        if (imageWidth > 0) void applyAutoStarMask(imageWidth, imageHeight, next, grow);
                      }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-800 accent-blue-600"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Grow mask</span>
                      <span className="font-mono">{grow}px</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mb-1">
                      Expands the mask outward to guarantee the whole mark is covered.
                    </p>
                    <input
                      type="range"
                      min="0"
                      max="20"
                      value={grow}
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        setGrow(next);
                        if (imageWidth > 0) void applyAutoStarMask(imageWidth, imageHeight, coverage, next);
                      }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-800 accent-blue-600"
                    />
                  </div>
                </div>

                <div className="border-t border-gray-100 dark:border-slate-800 pt-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">
                      Fill Method
                    </label>
                    <select
                      value={inpaintMethod}
                      onChange={(e) => setInpaintMethod(e.target.value as InpaintMethod)}
                      className="w-full h-9 px-2 border border-gray-200 rounded-lg text-xs bg-white dark:bg-slate-950 dark:border-slate-800 cursor-pointer"
                    >
                      <option value="telea">Telea (best for thin marks)</option>
                      <option value="ns">Navier-Stokes (smoother)</option>
                    </select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-[10px] uppercase font-bold text-gray-400 mb-1">
                      <span>Fill Radius</span>
                      <span className="font-mono">{inpaintRadius}px</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      value={inpaintRadius}
                      onChange={(e) => setInpaintRadius(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-800 accent-blue-600 mt-3"
                    />
                  </div>
                </div>
              </div>
            )}

            {removalMode === 'crop' && (
              <div className="col-span-2">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>Corner size to trim</span>
                  <span className="font-mono">{cropSize}px</span>
                </div>
                <p className="text-[10px] text-gray-400 mb-1">
                  Trims the right and bottom edges so the corner watermark falls outside the
                  image. This throws away real image content, so prefer inpainting when you can.
                </p>
                <input
                  type="range"
                  min="32"
                  max="600"
                  value={cropSize}
                  onChange={(e) => setCropSize(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-800 accent-blue-600"
                />
              </div>
            )}

            <div className="col-span-2">
              <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">
                Background Processing
              </label>
              <select
                value={bgRemoval}
                onChange={(e) => setBgRemoval(e.target.value as 'none' | 'magic-wand')}
                className="w-full text-sm bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-lg p-2 text-gray-700 dark:text-gray-300"
              >
                <option value="none">None (keep original background)</option>
                <option value="magic-wand">Remove flat white/checkerboard background</option>
              </select>

              {bgRemoval === 'magic-wand' && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-lg">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Colour tolerance</span>
                    <span className="font-mono w-8 text-right">{bgTolerance}</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mb-2">
                    Lower deletes less; raise it if background fragments survive.
                  </p>
                  <input
                    type="range"
                    min="1"
                    max="40"
                    value={bgTolerance}
                    onChange={(e) => setBgTolerance(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-800 accent-blue-600"
                  />
                </div>
              )}
            </div>

            <div className="col-span-1">
              <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">
                Upscale Factor
              </label>
              <select
                value={scaleFactor}
                onChange={(e) => setScaleFactor(Number(e.target.value))}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white dark:bg-slate-950 dark:border-slate-800 cursor-pointer"
              >
                <option value={1}>1x (original size)</option>
                <option value={2}>2x (high res)</option>
                <option value={3}>3x (ultra res)</option>
                <option value={4}>4x (max res)</option>
              </select>
            </div>

            <div className="col-span-1">
              <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">
                Target DPI (print ready)
              </label>
              <select
                value={targetDpi}
                onChange={(e) => setTargetDpi(Number(e.target.value))}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white dark:bg-slate-950 dark:border-slate-800 cursor-pointer"
              >
                <option value={0}>Default (screen 72 DPI)</option>
                <option value={150}>150 DPI (draft print)</option>
                <option value={300}>300 DPI (high quality print)</option>
              </select>
            </div>

            <div className="col-span-2">
              <div className="flex items-center justify-between">
                <label className="text-[10px] uppercase font-bold text-gray-400">
                  Sharpening (unsharp mask)
                </label>
                <span className="font-mono text-[10px] text-blue-500">
                  {sharpenAmount === 0 ? 'Off' : sharpenAmount.toFixed(2)}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={sharpenAmount}
                onChange={(e) => setSharpenAmount(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-slate-800 accent-blue-600 mt-1"
              />
              <p className="text-[10px] text-gray-400 mt-1">
                0 = soft · 0.45 = crisp (recommended) · 1 = aggressive
              </p>
            </div>
          </div>

          <Button
            className="w-full bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
            onClick={processImage}
            disabled={!canProcess}
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                {status ?? 'Processing…'}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" /> Process image
              </>
            )}
          </Button>
        </Card>
      </div>

      <div className="space-y-6">
        <Card className="p-6 bg-slate-50 border-slate-200 dark:bg-slate-900/40 dark:border-slate-800 flex flex-col justify-center min-h-[400px]">
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4">Result</h3>

          <canvas ref={canvasRef} className="hidden" />

{result ? (
            <div className="space-y-4 flex flex-col items-center w-full">
              {resultBytes !== null && resultBytes > PREVIEW_LIMIT_BYTES ? (
                <div className="flex flex-col items-center justify-center p-8 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-center w-full min-h-[200px]">
                  <ImageIcon size={32} className="text-gray-400 mb-2" />
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Preview unavailable
                  </span>
                  <p className="text-xs text-gray-500 max-w-xs mt-1">
                    The upscaled image is {formatBytes(resultBytes)}. It is ready to download, but
                    too large to render smoothly here.
                  </p>
                </div>
              ) : (
                <img
                  src={result}
                  alt="Processed result"
                  className="max-h-64 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 object-contain max-w-full bg-checkerboard"
                />
              )}
              <Button
                onClick={handleDownload}
                className="gap-2 cursor-pointer w-full md:w-auto"
                variant="secondary"
              >
                <Download size={16} /> Download result
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400 dark:text-gray-600 space-y-4 h-full min-h-[250px]">
              <ImageIcon size={48} className="opacity-20" />
              <p className="text-sm">Your result will appear here.</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

/**
 * Above this encoded size we skip the inline preview.
 *
 * Browsers do render large bitmaps, but decoding a very large PNG into the
 * preview costs seconds of main-thread time and a lot of memory for no benefit —
 * the result is displayed at 256px tall.
 */
const PREVIEW_LIMIT_BYTES = 6_000_000;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Flood-fill the background to transparent, in place.
 *
 * Seeding only from the border is what makes this safe: pixels that merely
 * happen to match the background colour inside the subject are left alone,
 * because nothing connects them to the edge.
 *
 * Operates on raw RGBA so it can run before the pixels are handed to the worker.
 */
function knockOutBackgroundPixels(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  tolerance: number
): void {
  const model = getBackgroundModel(data, width, height);

  const isBackground = (r: number, g: number, b: number, x: number, y: number): boolean => {
    if (data[(y * width + x) * 4 + 3] === 0) return false;

    const vR = model.c1.r - model.c2.r;
    const vG = model.c1.g - model.c2.g;
    const vB = model.c1.b - model.c2.b;
    const vLenSq = vR * vR + vG * vG + vB * vB;

    if (vLenSq < 5) {
      // Solid-colour background: fall back to a channel-wise distance.
      const d = Math.max(
        Math.abs(r - model.c1.r),
        Math.abs(g - model.c1.g),
        Math.abs(b - model.c1.b)
      );
      return d <= tolerance;
    }

    // Project onto the line between the two sampled border colours, then measure
    // a Chebyshev distance so it shares the tolerance slider's units.
    const wR = r - model.c2.r;
    const wG = g - model.c2.g;
    const wB = b - model.c2.b;
    let t = (wR * vR + wG * vG + wB * vB) / vLenSq;
    t = Math.max(-0.5, Math.min(1.5, t));

    const projR = model.c2.r + t * vR;
    const projG = model.c2.g + t * vG;
    const projB = model.c2.b + t * vB;
    const d = Math.max(Math.abs(r - projR), Math.abs(g - projG), Math.abs(b - projB));
    return d <= tolerance;
  };

  const visited = new Uint8Array(width * height);
  const stack = new Int32Array(width * height);
  let sp = 0;

  const push = (x: number, y: number): void => {
    if (x < 0 || x >= width || y < 0 || y >= height) return;
    const idx = y * width + x;
    if (visited[idx]) return;
    visited[idx] = 1;
    stack[sp++] = idx;
  };

  // Seeds are spaced rather than exhaustive: 50px is fine for a flat background
  // and keeps the initial fill bounded on very large images.
  const seedStep = 50;
  for (let x = 0; x < width; x += seedStep) {
    push(x, 0);
    push(x, height - 1);
  }
  for (let y = 0; y < height; y += seedStep) {
    push(0, y);
    push(width - 1, y);
  }

  while (sp > 0) {
    const idx = stack[--sp];
    const x = idx % width;
    const y = (idx / width) | 0;
    const p = idx * 4;

    if (isBackground(data[p], data[p + 1], data[p + 2], x, y)) {
      data[p + 3] = 0;
      push(x - 1, y);
      push(x + 1, y);
      push(x, y - 1);
      push(x, y + 1);
      // Diagonals as well, so checkerboard corners do not leave seams.
      push(x - 1, y - 1);
      push(x + 1, y - 1);
      push(x - 1, y + 1);
      push(x + 1, y + 1);
    }
  }
}
